import { Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import NotFoundFullScreenMessage from "../../../components/fullScreenMessage/NotFoundFullScreenMessage";
import { fetchPetsTab } from "../../../enums/queryFunction/Fetch";
import { PETS_GENERAL } from "../../../enums/QueryKeys";
import { GENERAL } from "../enums/TabsRequestParams";
import DeceasedPet from "../form/DeceasedPet";
import DeletePet from "../form/DeletePet";
import CustomAccordion from "../../../components/display/CustomAccordion";

export default function PetManagement() {
  const { t } = useTranslation();
  const { id: petId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: [PETS_GENERAL + petId],
    queryFn: () => fetchPetsTab(GENERAL, petId),
    staleTime: Infinity,
  });

  const isDeceased = data?.deceased;

  const deletePetData = {
    title: "DeletePet",
    component: <DeletePet />,
    showTitle: true,
  };

  const deceasedPetData = {
    title: "DeceasedPet",
    component: <DeceasedPet pet={data} />,
    showTitle: true,
  };

  const permanent = [
    {
      title: "PermanentActions",
      items: [...(isDeceased ? [] : [deceasedPetData]), deletePetData],
      expanded: true,
    },
  ];

  const petManagmentSettingsGroups = [...permanent];

  if (isLoading) {
    return <LinearProgress />;
  }

  if (error) {
    if (String(error?.response?.data).toUpperCase() === String("FORBIDDEN")) {
      navigate(-1);
      return;
    }
    return <NotFoundFullScreenMessage testId="pet.management.not-found" />;
  }

  return (
    <Stack spacing={3}>
      {petManagmentSettingsGroups.map((group, groupIndex) => (
        <CustomAccordion
          key={groupIndex}
          title={group.title}
          defaultExpanded={group.expanded}
          testId="pet.management.permanent-actions"
        >
          <Stack spacing={2}>
            {group.items.map((item, itemIndex) => (
              <React.Fragment key={itemIndex}>
                <Stack spacing={1}>
                  {item.showTitle ? (
                    <Typography
                      variant="body2"
                      color={item.textColor || "text.secondary"}
                    >
                      {t(item.title)}
                    </Typography>
                  ) : null}
                  {item.component}
                </Stack>
                {itemIndex < group.items.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </React.Fragment>
            ))}
          </Stack>
        </CustomAccordion>
      ))}
    </Stack>
  );
}
